import d3 = require("d3");
import * as React from "react";
import {DataPoint, DataPointWithID} from "./common";

interface Props {
    data?: DataPointWithID[];
    containerClassName?: string;
    barsClassName?: string;
    labelClassName?: string;
    startingBarFillColor?: d3.ValueFn<d3.BaseType, DataPoint, string | number | boolean>;
    barFillColor?: d3.ValueFn<d3.BaseType, DataPoint, string | number | boolean>;
    svgRef?: React.RefObject<any>;
    withSize?: any;
    unitOfMeasure?: string;
    maxValue?: number;
}

interface State {
    width: number;
    height: number;
}

const DEFAULT_TRANSITION_DURATION = 300;

export class Histogram extends React.Component<Props, State> {

    private svgRef: React.RefObject<any>;

    constructor(props) {
        super(props);
        this.svgRef = React.createRef();
        this.state = {width: props.withSize.width, height: props.withSize.height};
    }

    public componentDidMount() {
        this.initHistogram();
        this.drawHistogram(this.props.data);
    }

    public componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<any>, snapshot?: any) {
        if (
            prevProps.data !== this.props.data
            || prevProps.withSize !== this.props.withSize
            || prevProps.maxValue !== this.props.maxValue
        ) {
            this.drawHistogram(this.props.data);
        }
    }

    public render() {
        return (<svg className={this.props.containerClassName} ref={this.svgRef}/>);
    }

    private initHistogram = () => {

        const rHeight: number = this.svgRef.current.clientHeight;
        const rWidth: number = this.svgRef.current.clientWidth;

        const bottomLabelBarHeight = 20;

        const svg = d3.select(this.svgRef.current);

        this.createGradient(svg);

        svg.append("g")
            .attr("class", "horizontal-grid-lines");

        svg.append("g")
            .attr("class", "chart-bg")
            .append("g")
            .attr("class", "bars");

        svg.append("g")
            .attr("class", "vertical-grid-lines")
            .attr("transform", "translate(0," + rHeight + ")");

        const bottomLabels = svg.append("g")
            .attr("class", "bottom-labels-bg");

        bottomLabels.append("rect")
            .attr("class", "bottom-labels-rect-bg");

        bottomLabels.append("g")
            .attr("class", "labels-dividers")
            .attr("transform", "translate(0," + (rHeight - bottomLabelBarHeight * .7) + ")");
    }

    private drawHistogram = (dataForChart: DataPointWithID[]) => {
        const rHeight: number = this.svgRef.current.clientHeight;
        const rWidth: number = this.svgRef.current.clientWidth;

        const data: DataPointWithID[] = dataForChart.map((elem: DataPointWithID) => {
            return {
                ...elem,
                value: isNaN(elem.value) ? 0 : elem.value
            }
        })
        const xScale = d3.scaleBand()
            .domain(data.map((d) => ("" + d.id || d.label)))
            .range([0, rWidth])
            .padding(0);

        const values = data.map((d) => d.value);

        let maxValue = this.props.maxValue || Math.max(...values);

        maxValue = maxValue > 0 ? maxValue : 1;

        const minValue = 0;

        const bottomLabelBarHeight = 20;

        const chartHeight = rHeight - bottomLabelBarHeight;

        const yScale = d3.scaleLinear()
            .domain([minValue, maxValue])
            .range([chartHeight, 20]);

        const xAxisGrid = d3.axisBottom(d3.scaleLinear().domain([0, data.length]).range([0, rWidth]))
            .tickSize(-rHeight)
            .ticks(data.length);

        const svg = d3.select(this.svgRef.current)
            .classed(this.props.containerClassName, true);

        const yAxisGrid = d3.axisLeft(yScale)
            .tickSize(-rWidth)
            .tickFormat(((d) => {
                if (d <= 0) {
                    return "";
                }
                return d + (this.props.unitOfMeasure || "");
            }) as any)
            .ticks(10);

        /*svg.selectAll(".gradient-bg")
            .attr("width", rWidth)
            .attr("height", rHeight);*/

        svg.selectAll(".horizontal-grid-lines")
            .transition()
            .duration(DEFAULT_TRANSITION_DURATION)
            .call(yAxisGrid as any);

        svg.selectAll(".vertical-grid-lines")
            .transition()
            .duration(DEFAULT_TRANSITION_DURATION)
            .call(xAxisGrid as any);

        svg.select(".vertical-grid-lines")
            .attr("transform", "translate(0," + rHeight + ")");

        const chart = svg.selectAll("g.bars");

        const rects = chart.selectAll(".bar")
            .data(data, ((d: DataPointWithID) => d.id || d.label) as any);

        const oldRects = rects
            .exit()
            .transition()
            .duration(DEFAULT_TRANSITION_DURATION)
            .attr("height", rHeight - yScale(0))
            .attr("y", yScale(0))
            .remove();

        const newRects = rects.enter()
            .append("rect")
            .classed("bar", true)
            .attr("width", xScale.bandwidth())
            .attr("height", rHeight - yScale(0))
            .attr("x", (d) => xScale("" + (d.id || d.label)) as number)
            .attr("fill", this.props.startingBarFillColor || this.props.barFillColor)
            .attr("opacity", 0.7)
            .attr("y", yScale(0));

        newRects.merge(rects as any)
            .transition()
            .duration(DEFAULT_TRANSITION_DURATION)
            .attr("fill", this.props.barFillColor)
            .attr("height", (d) => {
                const h = chartHeight - yScale(d.value);
                return chartHeight - (0 < h && h < 5 ? chartHeight - 5 : yScale(d.value));
            })
            .attr("y", (d) => {
                const h = chartHeight - yScale(d.value);
                return 0 < h && h < 5 ? chartHeight - 5 : yScale(d.value);
            })
            .attr("x", (d) => xScale("" + (d.id || d.label)) as number)
            .attr("width", xScale.bandwidth());

        // bottom labels
        const bottomBar = svg.selectAll("g.bottom-labels-bg");

        bottomBar.selectAll(".bottom-labels-rect-bg")
            .attr("fill", "var(--viewport-bg)")
            .attr("height", bottomLabelBarHeight)
            .attr("y", rHeight - bottomLabelBarHeight)
            .attr("x", 0)
            .attr("width", rWidth);

        const xAxis = d3.axisBottom(d3.scaleLinear().domain([0, data.length]).range([0, rWidth]))
            .tickSize(-bottomLabelBarHeight * .3)
            .tickFormat("" as any)
            .ticks(data.length);

        bottomBar.selectAll(".labels-dividers")
            .transition()
            .duration(DEFAULT_TRANSITION_DURATION)
            .call(xAxis as any);

        bottomBar.select(".labels-dividers")
            .attr("transform", "translate(0," + (rHeight - bottomLabelBarHeight * .7) + ")");

        const labels = bottomBar.selectAll(".bar-labels")
            .data(data, ((d: DataPointWithID) => d.id || d.label) as any);

        const oldLabels = labels
            .exit()
            .remove();

        const newLabels = labels.enter()
            .append("text")
            .classed("bar-labels", true);

        newLabels.merge(labels as any)
            .transition()
            .duration(DEFAULT_TRANSITION_DURATION)
            .text((d) => d.label)
            .attr("x", (d) => xScale("" + (d.id || d.label)) + xScale.bandwidth() / 2)
            .attr("y", rHeight - 5);

        svg.selectAll(".horizontal-grid-lines")
            .selectAll("line")
            .style("transform", "translate(35px, 0)");
    }

    private createGradient = (svg: d3.Selection<any, {}, null, undefined>) => {
        const gradient = svg.append("defs")
            .append("linearGradient")
            .attr("id", "histogramGradientBg")
            .attr("gradientUnits", "objectBoundingBox")
            .attr("x1", "0%")
            .attr("x2", "0%")
            .attr("y1", "0%")
            .attr("y2", "100%");

        gradient.append("stop")
            .attr("offset", "0%")
            .attr("stop-color", "#ffffff");

        gradient.append("stop")
            .attr("offset", "100%")
            .attr("stop-color", "#cacaca");
    }
}
